import React, { useEffect, useRef, useState } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { Box, Paper, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Alert, Switch, Checkbox } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import DocumentUploader from '../documents/DocumentUploader';
import DocumentManager from '../documents/DocumentManager';
import EmptyState from '../common/EmptyState';

// Dedicated Experts management page (feature-flagged via router and sidebar)
export default function ExpertsPage() {
  const { confirmDialogProps, confirm } = useConfirmDialog();

  const FEATURE_EXPERTS = (process.env.REACT_APP_FEATURE_EXPERTS ?? 'true') === 'true';

  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(localStorage.getItem('expertsSelectedBrandId') || '');
  const [loadingBrands, setLoadingBrands] = useState(false);

  const [experts, setExperts] = useState([]);
  const [expertsLoading, setExpertsLoading] = useState(false);
  const [selectedExpertId, setSelectedExpertId] = useState(localStorage.getItem('expertsSelectedExpertId') || '');

  // Create/Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpert, setEditingExpert] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'analyst', personality: '', instructions: '', starter_prompts: '' });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [saving, setSaving] = useState(false);

  // Knowledge ingestion state
  const [knowledgeText, setKnowledgeText] = useState('');
  const [knowledgeFileName, setKnowledgeFileName] = useState('');
  const [ingesting, setIngesting] = useState(false);
  const [knowledgeTab, setKnowledgeTab] = useState(0); // 0 = text, 1 = documents, 2 = manage, 3 = permissions

  // Agent tools state (reads from mcp_agent_tools)
  const [agentTools, setAgentTools] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [addToolOpen, setAddToolOpen] = useState(false);
  const [savingTools, setSavingTools] = useState(false);
  // DT-2017 sibling: a failed load of the agent's tools / users is shown and its controls are hidden, because
  // each save replaces the agent's whole list and must never be built from a guessed [].
  const [toolsLoadError, setToolsLoadError] = useState('');
  const [usersLoadError, setUsersLoadError] = useState('');
  // Only the latest load may fill the list: a late answer for the previously selected agent is ignored.
  const toolsLoadSeq = useRef(0);
  const usersLoadSeq = useRef(0);

  // Skills state (Phase 4)
  const [skillsList, setSkillsList] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillCategories, setSkillCategories] = useState([]);
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [skillForm, setSkillForm] = useState({
    name: '',
    description: '',
    category: 'analytics',
    instructions: '',
    allowed_expert_types: ['all'],
    required_tools: ['clickhouse-advanced'],
    version: '1.0'
  });
  const [savingSkill, setSavingSkill] = useState(false);

  // User access state (Phase 6)
  const [companyUsers, setCompanyUsers] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);

  useEffect(() => {
    if (!FEATURE_EXPERTS) return;
    (async () => {
      try {
        setLoadingBrands(true);
        const resp = await axios.get('/api/chat/brands');
        setBrands(resp.data || []);
        if (!selectedBrandId && (resp.data || []).length > 0) {
          setSelectedBrandId(resp.data[0].id);
        }
      } catch (e) {
        console.error('Failed to load brands', e);
      } finally {
        setLoadingBrands(false);
      }
    })();
  }, [FEATURE_EXPERTS]);

  useEffect(() => {
    if (!FEATURE_EXPERTS || !selectedBrandId) return;
    localStorage.setItem('expertsSelectedBrandId', selectedBrandId);
    loadExperts(selectedBrandId);
    // Reset expert selection when brand changes
    setSelectedExpertId('');
    localStorage.removeItem('expertsSelectedExpertId');
  }, [FEATURE_EXPERTS, selectedBrandId]);

  // Manage selectedExpertId localStorage
  useEffect(() => {
    if (selectedExpertId) {
      localStorage.setItem('expertsSelectedExpertId', selectedExpertId);
    } else {
      localStorage.removeItem('expertsSelectedExpertId');
    }
  }, [selectedExpertId]);

  // Auto-select first expert when experts load
  useEffect(() => {
    if (experts.length > 0 && !selectedExpertId) {
      setSelectedExpertId(experts[0].id.toString());
    }
  }, [experts, selectedExpertId]);

  // Load agent tools when selected expert changes or Tool Permissions tab is active
  useEffect(() => {
    if (knowledgeTab === 3 && selectedExpertId) {
      loadAgentToolsList(selectedExpertId);
      if (allTools.length === 0) loadAllTools();
    }
  }, [knowledgeTab, selectedExpertId]);

  // Load skills and all tools when component mounts (Phase 4)
  useEffect(() => {
    if (!FEATURE_EXPERTS) return;
    loadSkills();
    loadSkillCategories();
    loadAllTools();
  }, [FEATURE_EXPERTS]);

  // Load user access data when User Access tab is selected (Phase 6)
  useEffect(() => {
    if (knowledgeTab === 5 && selectedExpertId) {
      loadAgentUsers(selectedExpertId);
      if (companyUsers.length === 0) loadCompanyUsers();
    }
  }, [knowledgeTab, selectedExpertId]);

  const loadAgentToolsList = async (agentId) => {
    const seq = ++toolsLoadSeq.current;
    try {
      setToolsLoading(true);
      setToolsLoadError('');
      const resp = await axios.get(`/api/mcp-admin/agents/${agentId}/tools`);
      if (seq !== toolsLoadSeq.current) return;
      setAgentTools(resp.data || []);
    } catch (e) {
      console.error('Failed to load agent tools', e);
      if (seq !== toolsLoadSeq.current) return;
      setAgentTools([]);
      setToolsLoadError('Could not load the tools assigned to this agent, so they are not shown and cannot be changed. Reopen this tab to try again.');
    } finally {
      if (seq === toolsLoadSeq.current) setToolsLoading(false);
    }
  };

  const loadAllTools = async () => {
    try {
      const resp = await axios.get('/api/mcp-admin/tools');
      setAllTools(resp.data || []);
    } catch (e) {
      console.error('Failed to load all tools', e);
    }
  };

  const addToolToAgent = async (toolId) => {
    if (toolsLoadError) return;
    setSavingTools(true);
    try {
      const currentIds = agentTools.map(t => t.tool_id);
      await axios.put(`/api/mcp-admin/agents/${selectedExpertId}/tools`, {
        tool_ids: [...currentIds, toolId]
      });
      setAddToolOpen(false);
      loadAgentToolsList(selectedExpertId);
    } catch (e) {
      console.error('Failed to add tool', e);
      alert('Failed to add tool: ' + (e.response?.data?.message || e.message));
    } finally {
      setSavingTools(false);
    }
  };

  const removeToolFromAgent = async (toolId) => {
    if (toolsLoadError) return;
    if (!await confirm({ title: 'Remove Tool', message: 'Remove this tool from the agent?', confirmText: 'Remove', severity: 'warning' })) return;
    setSavingTools(true);
    try {
      const currentIds = agentTools.map(t => t.tool_id).filter(id => id !== toolId);
      await axios.put(`/api/mcp-admin/agents/${selectedExpertId}/tools`, {
        tool_ids: currentIds
      });
      loadAgentToolsList(selectedExpertId);
    } catch (e) {
      console.error('Failed to remove tool', e);
      alert('Failed to remove tool');
    } finally {
      setSavingTools(false);
    }
  };

  // Skills functions (Phase 4)
  const loadSkills = async () => {
    try {
      setSkillsLoading(true);
      const resp = await axios.get('/api/chat/skills/all');
      setSkillsList(resp.data?.skills || []);
    } catch (e) {
      console.error('Failed to load skills', e);
      setSkillsList([]);
    } finally {
      setSkillsLoading(false);
    }
  };

  const loadSkillCategories = async () => {
    try {
      const resp = await axios.get('/api/chat/skill-categories');
      setSkillCategories(resp.data?.categories || ['analytics', 'marketing', 'operations', 'finance', 'custom']);
    } catch (e) {
      console.error('Failed to load skill categories', e);
      setSkillCategories(['analytics', 'marketing', 'operations', 'finance', 'custom']);
    }
  };

  const openAddSkill = () => {
    setEditingSkill(null);
    setSkillForm({
      name: '',
      description: '',
      category: 'analytics',
      instructions: '',
      allowed_expert_types: ['all'],
      required_tools: ['clickhouse-advanced'],
      version: '1.0'
    });
    setSkillModalOpen(true);
  };

  const openEditSkill = (skill) => {
    setEditingSkill(skill);
    setSkillForm({
      name: skill.name || '',
      description: skill.description || '',
      category: skill.category || 'analytics',
      instructions: skill.instructions || '',
      allowed_expert_types: skill.allowed_expert_types || ['all'],
      required_tools: skill.required_tools || ['clickhouse-advanced'],
      version: skill.version || '1.0'
    });
    setSkillModalOpen(true);
  };

  const saveSkill = async () => {
    setSavingSkill(true);
    try {
      if (editingSkill) {
        // Update existing skill
        await axios.put(`/api/chat/skills/${editingSkill.id}`, skillForm);
      } else {
        // Create new skill
        await axios.post('/api/chat/skills', skillForm);
      }
      setSkillModalOpen(false);
      loadSkills();
    } catch (e) {
      console.error('Failed to save skill', e);
      alert('Failed to save skill: ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingSkill(false);
    }
  };

  const deleteSkill = async (skill) => {
    if (!await confirm({ title: 'Delete Skill', message: `Delete skill "${skill.name}"? This cannot be undone.`, confirmText: 'Delete', severity: 'danger' })) return;

    try {
      await axios.delete(`/api/chat/skills/${skill.id}?hard=true`);
      loadSkills();
    } catch (e) {
      console.error('Failed to delete skill', e);
      alert('Failed to delete skill: ' + (e.response?.data?.error || e.message));
    }
  };

  const toggleSkillActive = async (skill) => {
    try {
      if (skill.is_active) {
        // Deactivate (soft delete)
        await axios.delete(`/api/chat/skills/${skill.id}`);
      } else {
        // Reactivate
        await axios.post(`/api/chat/skills/${skill.id}/reactivate`);
      }
      loadSkills();
    } catch (e) {
      console.error('Failed to toggle skill status', e);
      alert('Failed to update skill status');
    }
  };

  // --- User Access functions (Phase 6) ---
  const loadCompanyUsers = async () => {
    try {
      const resp = await axios.get('/api/mcp-admin/company-users');
      setCompanyUsers(resp.data || []);
    } catch (e) {
      console.error('Failed to load company users', e);
    }
  };

  const loadAgentUsers = async (agentId) => {
    const seq = ++usersLoadSeq.current;
    try {
      setUsersLoading(true);
      setUsersLoadError('');
      const resp = await axios.get(`/api/mcp-admin/agents/${agentId}/users`);
      if (seq !== usersLoadSeq.current) return;
      setAssignedUserIds((resp.data || []).map(u => u.user_id));
    } catch (e) {
      console.error('Failed to load agent users', e);
      if (seq !== usersLoadSeq.current) return;
      setAssignedUserIds([]);
      setUsersLoadError('Could not load the users who can access this agent, so they are not shown and cannot be changed. Reopen this tab to try again.');
    } finally {
      if (seq === usersLoadSeq.current) setUsersLoading(false);
    }
  };

  const saveAgentUsers = async () => {
    if (!selectedExpertId || usersLoadError) return;
    try {
      setSavingUsers(true);
      await axios.put(`/api/mcp-admin/agents/${selectedExpertId}/users`, { user_ids: assignedUserIds });
    } catch (e) {
      console.error('Failed to save agent users', e);
      alert('Failed to save user access: ' + (e.response?.data?.message || e.message));
    } finally {
      setSavingUsers(false);
    }
  };

  const toggleUserAccess = (userId) => {
    setAssignedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const loadExperts = async (brandId) => {
    try {
      setExpertsLoading(true);
      const resp = await axios.get(`/api/chat/agents/${brandId}`);
      setExperts(resp.data?.experts || []);
    } catch (e) {
      console.error('Failed to load agents', e);
      setExperts([]);
    } finally {
      setExpertsLoading(false);
    }
  };

  const TEMPLATES = {
    analyst: {
      name: 'Analyst',
      type: 'analyst',
      personality: 'Data-driven, curious, cross-functional; connects insights across departments.',
      instructions: `- Query ClickHouse database to uncover patterns and trends across all business areas.
- Synthesize insights from marketing, sales, operations, and customer data.
- Identify correlations and anomalies that drive business outcomes.
- Provide actionable recommendations with supporting data and confidence levels.
- Ask clarifying questions to understand the business context before diving into analysis.`,
      starter_prompts: [
        'What are the key performance trends across all departments this month?',
        'Show me correlations between marketing spend and revenue by channel.',
        'Which customer segments are driving the most growth and why?',
        'What operational metrics are impacting customer satisfaction?'
      ]
    },
    head_of_brand: {
      name: 'Head of Brand',
      type: 'head_of_brand',
      personality: 'Vision-led, consistent, long-term equity focus; crisp and strategic.',
      instructions: `- Maintain consistent tone and positioning across channels.
- Assess brand equity signals (direct traffic, branded organic, NPS) PoP/MoM.
- Identify message/creative themes that lift aided/unaided recall.
- Recommend 3 high-leverage brand initiatives with expected impact.`,
      starter_prompts: [
        'What signals show brand equity improved or declined this month?',
        'Which brand messages correlate with better repeat and referral?'
      ]
    },
    ecommerce_manager: {
      name: 'Ecommerce Manager',
      type: 'ecommerce_manager',
      personality: 'Conversion-focused, pragmatic, UX-minded; prioritizes impact/effort.',
      instructions: `- Diagnose funnel drop-offs (landing → PDP → cart → checkout).
- Rank fixes by impact/effort: speed, UX, trust, PDP enrichment.
- Recommend cross-sell/upsell bundles by affinity and margin.`,
      starter_prompts: [
        'Top 5 funnel frictions this week and fixes ranked by ROI.',
        'Which bundles/upsells should we add to boost AOV by 5–10%?'
      ]
    },
    retention_expert: {
      name: 'Retention Expert',
      type: 'retention_expert',
      personality: 'Lifecycle-oriented, LTV-first, thoughtful segmentation.',
      instructions: `- Analyze cohorts by RFM; surface at-risk segments and win-back levers.
- Identify top-performing flows and next tests (subject, offer, timing).
- Align CRM sends with inventory (avoid low-stock SKUs).`,
      starter_prompts: [
        'Which segments are at churn risk and what win-back offers convert best?',
        'What two flow tests will yield the biggest uplift this month?'
      ]
    },
    performance_marketing: {
      name: 'Performance Marketing',
      type: 'performance_marketing',
      personality: 'Data-driven, iterative, budget-allocation savvy.',
      instructions: `- Flag wasted spend; propose reallocation with projected ROAS/MER impact.
- Identify creative fatigue and new angles; suggest test cadence.
- Compare channels/devices PoP; call out incrementality risks.`,
      starter_prompts: [
        'Which campaigns underperformed vs last week and where to reallocate?',
        'What creative angles should we test next to lift CTR/CVR?'
      ]
    },
    operations: {
      name: 'Operations',
      type: 'operations',
      personality: 'Availability- and reliability-focused; minimizes friction.',
      instructions: `- Forecast demand by SKU/category; flag stock risks and overstock.
- Tie inventory signals to ad throttling/promo feasibility.
- Cluster CX tickets; quantify impact and propose fixes.`,
      starter_prompts: [
        'Which SKUs risk stockout and what ads/promos should we pause?',
        'Top CX issues driving returns or cart abandonment and fixes.'
      ]
    },
    content_seo: {
      name: 'Content & SEO',
      type: 'content_seo',
      personality: 'Compounding growth mindset; systematic and brief-driven.',
      instructions: `- Identify highest-revenue keyword gaps; generate content briefs.
- Recommend category page SEO (copy, facets, schema, internal links).
- Track organic by landing page; prioritize by potential revenue.`,
      starter_prompts: [
        'Top 5 content briefs to capture non-brand demand this quarter.',
        'Category page SEO improvements with expected revenue impact.'
      ]
    },
    finance: {
      name: 'Finance',
      type: 'finance',
      personality: 'Profitability-first; reconciles marketing and finance.',
      instructions: `- Reconcile platform vs analytics vs finance attributed revenue.
- Track MER, contribution margin post ad spend, and cash conversion.
- Flag unprofitable channels/SKUs after all costs.`,
      starter_prompts: [
        'Which channels are unprofitable after all costs this month?',
        'What is our MER and contribution margin trend PoP?'
      ]
    },
    creative_head: {
      name: 'Creative Head',
      type: 'creative_head',
      personality: 'Story-led; data-informed creative strategy.',
      instructions: `- Analyze creative performance by hook/angle/format.
- Identify fatigue windows and rotation cadence.
- Recommend 5 new concepts grounded in data.`,
      starter_prompts: [
        'Which creative angles drive the best CVR by audience/device?',
        'What new concepts should we brief to refresh fatigued ads?'
      ]
    },
    customer_service: {
      name: 'Customer Service',
      type: 'customer_service',
      personality: 'Customer-obsessed; resolves friction fast.',
      instructions: `- Cluster tickets to find systemic issues.
- Quantify impact on conversion/returns; propose fixes.
- Create FAQ/content updates to preempt tickets.`,
      starter_prompts: [
        'Top 3 recurring issues in tickets and proposed fixes with impact.',
        'Which SKUs drive disproportionate tickets? Content or quality fixes?'
      ]
    }
  };

  const allTypeOptions = [
    ['analyst', 'Analyst'],
    ['head_of_brand', 'Head of Brand'],
    ['ecommerce_manager', 'Ecommerce Manager'],
    ['retention_expert', 'Retention Expert'],
    ['performance_marketing', 'Performance Marketing'],
    ['operations', 'Operations'],
    ['content_seo', 'Content & SEO'],
    ['finance', 'Finance'],
    ['creative_head', 'Creative Head'],
    ['customer_service', 'Customer Service']
  ];

  const applyTemplate = (key) => {
    const tpl = TEMPLATES[key];
    if (!tpl) return;
    setSelectedTemplate(key);
    setForm({
      name: tpl.name,
      type: tpl.type,
      personality: tpl.personality,
      instructions: tpl.instructions,
      starter_prompts: (tpl.starter_prompts || []).join('\n')
    });
  };

  const openCreate = () => {
    setEditingExpert(null);
    setForm({ name: '', type: 'analyst', personality: '', instructions: '', starter_prompts: '' });
    setModalOpen(true);
  };

  const openEdit = (ex) => {
    setEditingExpert(ex);
    setForm({
      name: ex.name || '',
      type: ex.type || 'analyst',
      personality: ex.personality || '',
      instructions: ex.instructions || '',
      starter_prompts: Array.isArray(ex.starter_prompts) ? ex.starter_prompts.join('\n') : (ex.starter_prompts || '')
    });
    setModalOpen(true);
  };

  const saveExpert = async () => {
    if (!selectedBrandId) return;
    setSaving(true);
    try {
      // Map form fields to mcp_agents shape
      const agentPayload = {
        name: form.name,
        description: form.instructions || '',
        agent_type: form.type || 'general',
        personality_traits: form.personality || '',
        brand_id: parseInt(selectedBrandId),
      };

      if (editingExpert) {
        await axios.put(`/api/mcp-admin/agents/${editingExpert.id}`, agentPayload);
      } else {
        // Generate slug ID for new agents: slugified-name-brandId
        const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        agentPayload.id = `${slug}-${selectedBrandId}`;
        await axios.post('/api/mcp-admin/agents', agentPayload);
      }
      await loadExperts(selectedBrandId);
      setModalOpen(false);
    } catch (e) {
      console.error('Failed to save agent', e);
      alert('Failed to save agent: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const ingestKnowledge = async () => {
    if (!selectedBrandId || !selectedExpertId || !knowledgeText.trim()) return;
    try {
      setIngesting(true);
      const payload = {
        brandId: selectedBrandId,
        agentId: selectedExpertId,
        fileName: knowledgeFileName || 'pasted_text.txt',
        contentType: 'text/plain',
        text: knowledgeText,
      };
      const resp = await axios.post('/api/chat/expert-knowledge/text', payload);
      if (resp.status >= 200 && resp.status < 300) {
        setKnowledgeText('');
        alert('Knowledge uploaded successfully.');
      } else {
        alert('Upload did not complete successfully.');
      }
    } catch (e) {
      console.error('Knowledge upload failed', e);
      alert('Failed to upload knowledge');
    } finally {
      setIngesting(false);
    }
  };

  const deleteExpert = async (ex) => {
    if (!await confirm({ title: 'Delete Agent', message: `Delete agent "${ex.name}"? This cannot be undone.`, confirmText: 'Delete', severity: 'danger' })) return;
    try {
      await axios.delete(`/api/mcp-admin/agents/${ex.id}`);
      await loadExperts(selectedBrandId);
      if (selectedExpertId === ex.id.toString()) {
        setSelectedExpertId('');
      }
    } catch (e) {
      console.error('Failed to delete agent', e);
      alert('Failed to delete agent: ' + (e.response?.data?.message || e.message));
    }
  };

  if (!FEATURE_EXPERTS) {
    return (
      <Box p={2}>
        <Typography variant="h6">Experts feature is disabled</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>AI Agents</Typography>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Select Brand</InputLabel>
            <Select value={selectedBrandId} label="Select Brand" disabled={loadingBrands} onChange={(e) => setSelectedBrandId(e.target.value)}>
              {(brands || []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography>{b.name}</Typography>
                    {b.hasData && (
                      <Chip label={`${(b.totalRows || 0).toLocaleString()} rows`} size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!selectedBrandId}>
            New Agent
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {expertsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (experts || []).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">No agents yet for this brand.</Typography>
            <Button sx={{ mt: 2 }} variant="outlined" startIcon={<AddIcon />} onClick={openCreate} disabled={!selectedBrandId}>Create your first agent</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {experts.map((ex) => (
              <Paper 
                key={ex.id} 
                elevation={selectedExpertId === ex.id.toString() ? 3 : 1}
                sx={{ 
                  p: 3, 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: selectedExpertId === ex.id.toString() ? 'primary.main' : 'background.paper',
                  color: selectedExpertId === ex.id.toString() ? 'primary.contrastText' : 'text.primary',
                  border: selectedExpertId === ex.id.toString() ? 2 : 1,
                  borderColor: selectedExpertId === ex.id.toString() ? 'primary.main' : 'divider',
                  '&:hover': {
                    elevation: 2,
                    backgroundColor: selectedExpertId === ex.id.toString() ? 'primary.dark' : 'grey.50'
                  }
                }}
                onClick={() => setSelectedExpertId(ex.id.toString())}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{ex.name}</Typography>
                    <Chip 
                      label={ex.type} 
                      size="small" 
                      sx={{ 
                        textTransform: 'capitalize',
                        backgroundColor: selectedExpertId === ex.id.toString() ? 'rgba(255,255,255,0.2)' : 'primary.main',
                        color: selectedExpertId === ex.id.toString() ? 'inherit' : 'primary.contrastText'
                      }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(ex);
                      }}
                      sx={{ 
                        color: selectedExpertId === ex.id.toString() ? 'inherit' : 'action.active',
                        '&:hover': {
                          backgroundColor: selectedExpertId === ex.id.toString() ? 'rgba(255,255,255,0.1)' : 'action.hover'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteExpert(ex);
                      }}
                      sx={{ 
                        color: selectedExpertId === ex.id.toString() ? 'inherit' : 'action.active',
                        '&:hover': {
                          backgroundColor: selectedExpertId === ex.id.toString() ? 'rgba(255,255,255,0.1)' : 'action.hover'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                {ex.personality && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1, 
                      fontStyle: 'italic',
                      opacity: selectedExpertId === ex.id.toString() ? 0.9 : 0.7
                    }}
                  >
                    <strong>Personality:</strong> {ex.personality}
                  </Typography>
                )}
                
                {ex.instructions && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: selectedExpertId === ex.id.toString() ? 0.9 : 0.8,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    <strong>Instructions:</strong> {ex.instructions}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Enhanced Knowledge Management with Document Upload */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Knowledge Base</Typography>
          {selectedExpertId && (
            <Chip
              label={`Agent: ${experts.find(e => e.id.toString() === selectedExpertId)?.name || 'Unknown'}`}
              color="primary"
              size="small"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each agent has their own isolated knowledge base. Upload documents and text knowledge specific to each agent's role and expertise.
        </Typography>
        
        <Tabs value={knowledgeTab} onChange={(e, v) => setKnowledgeTab(v)} sx={{ mb: 3 }}>
          <Tab label="Text Input" />
          <Tab label="Document Upload" />
          <Tab label="Manage Documents" />
          <Tab label="Tool Permissions" icon={<SecurityIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Skills" icon={<AutoFixHighIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="User Access" icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
        
        {/* Text Input Tab */}
        {knowledgeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Optional File Name"
                size="small"
                value={knowledgeFileName}
                onChange={(e) => setKnowledgeFileName(e.target.value)}
                placeholder="e.g. brand_playbook.txt"
                sx={{ width: 280 }}
              />
              <Button variant="outlined" onClick={() => setKnowledgeText('')} disabled={!selectedBrandId}>Clear</Button>
            </Box>
            <TextField
              label="Knowledge Text"
              value={knowledgeText}
              onChange={(e) => setKnowledgeText(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              placeholder="Paste brand notes, product FAQs, tone guidelines, campaign plans, etc."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={ingestKnowledge}
                disabled={!selectedBrandId || !selectedExpertId || !knowledgeText.trim() || ingesting}
              >
                {ingesting ? 'Uploading…' : 'Upload Knowledge'}
              </Button>
              {(!selectedBrandId || !selectedExpertId) && (
                <Typography variant="caption" color="text.secondary">
                  {!selectedBrandId ? 'Select a brand to enable knowledge upload.' : 'Select an agent to upload knowledge for their specific knowledge base.'}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        
        {/* Document Upload Tab */}
        {knowledgeTab === 1 && (
          <Box>
            {selectedBrandId && selectedExpertId ? (
              <DocumentUploader
                brandId={parseInt(selectedBrandId)}
                agentId={selectedExpertId}
                onDocumentProcessed={(docId, data) => {
                  console.log('Document processed:', docId, data);
                  // Refresh document list if on manage tab
                  if (knowledgeTab === 2) {
                    // DocumentManager will handle its own refresh
                  }
                }}
              />
            ) : (
              <Typography color="text.secondary">
                {!selectedBrandId ? 'Select a brand to upload documents.' : 'Select an agent to upload documents for their knowledge base.'}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Manage Documents Tab */}
        {knowledgeTab === 2 && (
          <Box>
            <DocumentManager
              brandId={selectedBrandId ? parseInt(selectedBrandId) : null}
              agentId={selectedExpertId}
              onDocumentDeleted={(docId) => {
                console.log('Document deleted:', docId);
              }}
            />
          </Box>
        )}

        {/* Tool Permissions Tab */}
        {knowledgeTab === 3 && (
          <Box>
            {!selectedExpertId ? (
              <Alert severity="info">Select an agent to view their assigned tools.</Alert>
            ) : toolsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : toolsLoadError ? (
              <Alert severity="error">{toolsLoadError}</Alert>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    MCP tools assigned to this agent. These tools are available when users interact via chat.
                  </Typography>
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddToolOpen(true)}>
                    Add Tool
                  </Button>
                </Box>

                {agentTools.length === 0 ? (
                  <Alert severity="info">No tools assigned to this agent. Click "Add Tool" to assign one.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell><strong>Tool Name</strong></TableCell>
                          <TableCell><strong>Tool ID</strong></TableCell>
                          <TableCell align="center"><strong>Type</strong></TableCell>
                          <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentTools.map((tool, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{tool.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{tool.tool_id}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={tool.tool_type} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removeToolFromAgent(tool.tool_id)} title="Remove" color="error" disabled={savingTools}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Skills Tab (Phase 4) */}
        {knowledgeTab === 4 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Skills are procedural playbooks that guide AI agents on HOW to perform specific tasks.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When a user's question matches a skill's keywords, the skill's instructions are automatically injected into the agent's prompt.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadSkills} disabled={skillsLoading}>
                  Refresh
                </Button>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAddSkill}>
                  Add Skill
                </Button>
              </Box>
            </Box>

            {skillsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : skillsList.length === 0 ? (
              <EmptyState
                compact
                title="No skills configured yet"
                description="Click 'Add Skill' to create your first skill"
              />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="center"><strong>Expert Types</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Usage</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skillsList.map((skill) => (
                      <TableRow key={skill.id} sx={{ '&:hover': { backgroundColor: 'grey.50' }, opacity: skill.is_active ? 1 : 0.5 }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoFixHighIcon sx={{ fontSize: 16, color: skill.is_active ? 'primary.main' : 'text.disabled' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{skill.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={skill.category} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {skill.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {(skill.allowed_expert_types || ['all']).slice(0, 2).map((type, idx) => (
                              <Chip key={idx} label={type} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            ))}
                            {(skill.allowed_expert_types || []).length > 2 && (
                              <Chip label={`+${skill.allowed_expert_types.length - 2}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={!!skill.is_active}
                            onChange={() => toggleSkillActive(skill)}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`${skill.success_count || 0} successful out of ${skill.usage_count || 0} total uses`}>
                            <Typography variant="body2" color="text.secondary">
                              {skill.usage_count || 0}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openEditSkill(skill)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => deleteSkill(skill)} title="Delete" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Skills Summary */}
            {skillsList.length > 0 && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Skills Summary</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Total: ${skillsList.length}`} size="small" variant="outlined" />
                  <Chip label={`Active: ${skillsList.filter(s => s.is_active).length}`} color="success" size="small" variant="outlined" />
                  <Chip label={`Inactive: ${skillsList.filter(s => !s.is_active).length}`} color="default" size="small" variant="outlined" />
                  {skillCategories.map(cat => {
                    const count = skillsList.filter(s => s.category === cat).length;
                    return count > 0 ? (
                      <Chip key={cat} label={`${cat}: ${count}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* User Access Tab (Phase 6) */}
        {knowledgeTab === 5 && (
          <Box>
            {!selectedExpertId ? (
              <Alert severity="info">Select an agent to manage user access.</Alert>
            ) : usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : usersLoadError ? (
              <Alert severity="error">{usersLoadError}</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select which users can access this agent in chat. Only assigned users will see this agent.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Access</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyUsers.map(user => (
                      <TableRow key={user.id} hover sx={{ cursor: 'pointer' }} onClick={() => toggleUserAccess(user.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={assignedUserIds.includes(user.id)}
                            onChange={() => toggleUserAccess(user.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.full_name || '—'}</TableCell>
                        <TableCell>
                          {user.is_company_admin ? (
                            <Chip label="Admin" size="small" color="primary" variant="outlined" />
                          ) : (
                            <Chip label={user.role || 'user'} size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {companyUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    No company users found.
                  </Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Chip label={`${assignedUserIds.length} of ${companyUsers.length} users assigned`} size="small" variant="outlined" />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveAgentUsers}
                    disabled={savingUsers}
                  >
                    {savingUsers ? 'Saving…' : 'Save Access'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExpert ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select label="Template" value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {Object.entries(TEMPLATES).map(([key, tpl]) => (
                  <MenuItem key={key} value={key}>{tpl.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" />
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {allTypeOptions.map(([val,label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Personality" value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} fullWidth size="small" placeholder="Analytical, concise, data-driven" />
            <TextField label="Instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} fullWidth size="small" multiline minRows={4} placeholder="Detailed guidance and prompt template..." />
            <TextField label="Starter Prompts (one per line)" value={form.starter_prompts} onChange={(e) => setForm({ ...form, starter_prompts: e.target.value })} fullWidth size="small" multiline minRows={3} placeholder="E.g. Top 5 funnel frictions..." />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveExpert} disabled={!form.name || !form.type || saving}>{saving ? 'Saving...' : (editingExpert ? 'Save' : 'Create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Tool Dialog */}
      <Dialog open={addToolOpen} onClose={() => setAddToolOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Tool to Agent</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a tool to assign to this agent.
          </Typography>
          <List dense>
            {allTools
              .filter(t => !agentTools.some(at => at.tool_id === t.id))
              .map(tool => (
                <ListItem key={tool.id} disablePadding>
                  <ListItemButton onClick={() => addToolToAgent(tool.id)} disabled={savingTools}>
                    <ListItemText
                      primary={tool.name}
                      secondary={`${tool.id} — ${tool.tool_type}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            {allTools.filter(t => !agentTools.some(at => at.tool_id === t.id)).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                All available tools are already assigned.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToolOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Skill Edit Dialog (Phase 4) */}
      <Dialog open={skillModalOpen} onClose={() => setSkillModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingSkill ? 'Edit Skill' : 'Create Skill'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Skill Name"
                value={skillForm.name}
                onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                fullWidth
                size="small"
                placeholder="e.g. ROAS Analysis"
                disabled={!!editingSkill}
              />
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={skillForm.category}
                  onChange={(e) => setSkillForm({ ...skillForm, category: e.target.value })}
                >
                  {skillCategories.map(cat => (
                    <MenuItem key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>{cat}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Version"
                value={skillForm.version}
                onChange={(e) => setSkillForm({ ...skillForm, version: e.target.value })}
                size="small"
                sx={{ width: 100 }}
                placeholder="1.0"
              />
            </Box>

            <TextField
              label="Description"
              value={skillForm.description}
              onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder="Brief description of what this skill does. Include key terms that should trigger this skill."
              helperText="Keywords are automatically extracted from the description for skill matching."
            />

            <TextField
              label="Instructions"
              value={skillForm.instructions}
              onChange={(e) => setSkillForm({ ...skillForm, instructions: e.target.value })}
              fullWidth
              size="small"
              multiline
              minRows={10}
              placeholder={`Step-by-step instructions for the AI expert to follow.

Example:
## Objective
Calculate and analyze ROAS metrics for the brand.

## Steps
1. Query the ads_table to get spend and revenue data
2. Calculate ROAS = Revenue / Spend
3. Compare against benchmarks
4. Provide actionable recommendations

## SQL Template
\`\`\`sql
SELECT
  campaign_name,
  SUM(spend) as total_spend,
  SUM(revenue) as total_revenue,
  SUM(revenue) / NULLIF(SUM(spend), 0) as roas
FROM {database}.ads_table
WHERE date >= today() - 30
GROUP BY campaign_name
ORDER BY roas DESC
\`\`\`

Use {database} placeholder for brand-specific database.`}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Allowed Expert Types</InputLabel>
                <Select
                  label="Allowed Expert Types"
                  multiple
                  value={skillForm.allowed_expert_types}
                  onChange={(e) => setSkillForm({ ...skillForm, allowed_expert_types: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="all">All Experts</MenuItem>
                  {allTypeOptions.map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Required Tools</InputLabel>
                <Select
                  label="Required Tools"
                  multiple
                  value={skillForm.required_tools}
                  onChange={(e) => setSkillForm({ ...skillForm, required_tools: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {allTools.map(tool => (
                    <MenuItem key={tool.id} value={tool.id}>{tool.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Use placeholders like <code>{'{database}'}</code>, <code>{'{brand_name}'}</code>, and <code>{'{brand_id}'}</code> in your instructions.
                They will be automatically replaced with the current brand's values when the skill is used.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveSkill}
            disabled={!skillForm.name || !skillForm.description || !skillForm.instructions || savingSkill}
          >
            {savingSkill ? 'Saving...' : (editingSkill ? 'Save Changes' : 'Create Skill')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}

